export interface RawVersionActor {
  name: string | null;
  role: string | null;
}

export interface RawVersionChange {
  attribute: string;
  from: unknown;
  label: string;
  to: unknown;
}

export interface RawSubscriberVersion {
  actor: RawVersionActor;
  changes: RawVersionChange[];
  created_at: string;
  event: 'create' | 'destroy' | 'update';
  id: number;
  impersonated: RawVersionActor | null;
  program: string | null;
  staffer: string | null;
}

export interface AuditActor {
  name: string;
  role: string;
}

export interface AuditRow {
  actor: AuditActor;
  attribute: string;
  changedAt: string;
  from: string;
  id: string;
  impersonated: AuditActor | null;
  label: string;
  subject: string | null;
  to: string;
}

const EVENT_LABELS: Record<RawSubscriberVersion['event'], string> = {
  create: 'Subscriber created',
  destroy: 'Subscriber deleted',
  update: 'Updated',
};

// whodunnit_type is a class name (SupportManager) or a stamp (Console, Rake).
export const roleLabel = (role: string | null) => (role ?? 'Unknown').replace(/([a-z])([A-Z])/g, '$1 $2');

export const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (value === true) return 'On';
  if (value === false) return 'Off';
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.map(formatValue).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
};

const toActor = (raw: RawVersionActor | null): AuditActor | null => (
  raw ? { name: raw.name ?? 'Unknown', role: roleLabel(raw.role) } : null
);

// One version is one save; the log lists one row per attribute that save changed. A subscriber's
// own create or destroy carries no attribute diff, so it is a single row named by its event; a seat
// pool version (one with a program) always carries rows, "Program access: Added" for its create. The
// subject under the setting is the seat pool's program or the staffer version's teacher.
export const versionToRows = (version: RawSubscriberVersion): AuditRow[] => {
  const actor = toActor(version.actor) as AuditActor;
  const impersonated = toActor(version.impersonated);
  const base = {
    actor, changedAt: version.created_at, impersonated, subject: version.program ?? version.staffer,
  };

  if (version.event !== 'update' && !version.program) {
    return [{
      ...base,
      attribute: version.event,
      from: '',
      id: `${version.id}-${version.event}`,
      label: EVENT_LABELS[version.event],
      to: '',
    }];
  }

  return version.changes.map(change => ({
    ...base,
    attribute: change.attribute,
    from: formatValue(change.from),
    id: `${version.id}-${change.attribute}`,
    label: change.label,
    to: formatValue(change.to),
  }));
};
